import { API_URL, WS_URL } from '../config';
// src/contexts/NotificationContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { mergeNotifications, notificationKey } from '../utils/notificationIdentity';

export interface Notification {
  id: string;
  alertId?: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  category: 'system' | 'device' | 'maintenance' | 'safety' | 'fuel' | 'geofence' | 'vehicle_request';
  deviceId?: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  metadata?: any;
}

export interface ToastNotification {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  metadata?: any;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  hasMore: boolean;
  loading: boolean;
  toast: ToastNotification | null;
  dismissToast: () => void;
  loadMore: () => Promise<void>;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  socket: Socket | null;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// ─── Audio unlock ─────────────────────────────────────────────────────────────
// Browsers block autoplay until the user has interacted with the page.
// We pre-load and silently unlock the audio on the first click/keydown.
let audioUnlocked = false;
let audioElement: HTMLAudioElement | null = null;

function getAudio(): HTMLAudioElement {
  if (!audioElement) {
    audioElement = new Audio('/mixkit-message-pop-alert-2354.mp3');
    audioElement.volume = 0.5;
    audioElement.preload = 'auto';
  }
  return audioElement;
}

function unlockAudio() {
  if (audioUnlocked) return;
  const audio = getAudio();
  // Play silently to unlock — immediately pause so nothing is heard
  audio.volume = 0;
  audio.play()
    .then(() => {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 0.5;
      audioUnlocked = true;
      console.log('🔊 Audio unlocked');
    })
    .catch(() => { });
}

function playNotificationSound() {
  try {
    const audio = getAudio();
    audio.currentTime = 0;
    audio.volume = 0.5;
    audio.play().catch((err) => console.log('🔇 Sound blocked:', err.message));
  } catch {
    console.log('🔇 Audio not supported');
  }
}

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { token, user } = useAuth();
  const offsetRef = useRef(0);
  const requestRef = useRef(0);
  const receivedEventsRef = useRef(new Map<string, number>());
  const [serverUnreadCount, setServerUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<ToastNotification | null>(null);

  const refetchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const toastTimerRef = useRef<NodeJS.Timeout | null>(null);

  const unreadCount = Math.max(serverUnreadCount, notifications.filter((n) => !n.read).length);


  // ─── Audio unlock on first interaction ──────────────────────────────────
  useEffect(() => {
    const unlock = () => unlockAudio();
    window.addEventListener('click', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    window.addEventListener('touchstart', unlock, { once: true });
    return () => {
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
  }, []);

  // ─── Show toast ──────────────────────────────────────────────────────────
  const showToast = useCallback((data: ToastNotification) => {
    setToast(data);
    // Auto-dismiss after 6s for normal, 10s for critical
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    const duration = data.priority === 'critical' ? 10000 : 6000;
    toastTimerRef.current = setTimeout(() => setToast(null), duration);
  }, []);

  const dismissToast = useCallback(() => {
    setToast(null);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  // ─── Fetch notifications ─────────────────────────────────────────────────
  const fetchNotifications = useCallback(async (skip = 0, append = false) => {
    const request = ++requestRef.current;
    try {
      if (!token) return;

      setLoading(true);
      const LIMIT = 20;

      const response = await fetch(
        `${API_URL}/notifications?limit=${LIMIT}&skip=${skip}&unreadOnly=false&includeArchived=false`,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      if (response.ok && request === requestRef.current && localStorage.getItem('token') === token) {
        const data = await response.json();
        if (data.success && data.data) {
          const fetched: Notification[] = data.data.map((n: any) => ({
            id: n._id,
            alertId: n.alertId,
            type: n.type,
            category: n.category,
            title: n.title,
            message: n.message,
            timestamp: new Date(n.createdAt || n.timestamp),
            read: n.read,
            priority: n.priority,
            deviceId: n.deviceId,
            metadata: n.metadata || {},
          }));

          setNotifications((prev) => mergeNotifications(append ? [...prev, ...fetched] : fetched));
          offsetRef.current = skip + fetched.length;
          setServerUnreadCount(data.unreadCount ?? fetched.filter(n => !n.read).length);
          setHasMore(data.total != null ? offsetRef.current < data.total : fetched.length === LIMIT);
        }
      }
    } catch (err) {
      console.error('❌ Fetch notifications error:', err);
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [API_URL, token]);

  // Start after sign-in, and catch up even if a socket event was missed.
  useEffect(() => {
    requestRef.current++;
    offsetRef.current = 0;
    receivedEventsRef.current.clear();
    setNotifications([]);
    setServerUnreadCount(0);
    dismissToast();
    if (!token) { setLoading(false); return; }
    fetchNotifications();
    const refresh = () => {
      if (document.visibilityState === 'visible') fetchNotifications();
    };
    const interval = setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      requestRef.current++;
    };
  }, [token, user?._id, fetchNotifications, dismissToast]);

  // Debounced refetch
  const debouncedRefetch = useCallback(() => {
    if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
    refetchTimerRef.current = setTimeout(() => {
      fetchNotifications(0, false);
      refetchTimerRef.current = null;
    }, 500);
  }, [fetchNotifications]);

  // ─── Socket.IO ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) { setSocket(null); return; }

    console.log('🔌 Connecting WebSocket:', WS_URL);

    const newSocket = io(WS_URL, {
      transports: ['websocket', 'polling'],
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
    });

    newSocket.on('connect', () => {
      console.log('✅ WebSocket connected');
      debouncedRefetch();

      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          const companyId = user.companyId?._id || user.companyId;
          if (companyId) {
            console.log(`🏢 Joining company_${companyId}`);
            newSocket.emit('join_company_room', companyId);
          }
        } catch { }
      }
    });

    newSocket.on('room_joined', (data: any) => {
      console.log('✅ Joined room:', data.room);
    });

    newSocket.on('connect_error', (err) => {
      console.error('❌ Socket error:', err.message);
    });

    const acceptEvent = (data: any) => {
      const key = notificationKey(data);
      if (key === 'undefined') return true;
      const now = Date.now();
      const events = receivedEventsRef.current;
      for (const [id, time] of Array.from(events.entries())) {
        if (now - time > 5 * 60 * 1000) events.delete(id);
      }
      if (events.has(key)) return false;
      events.set(key, now);
      return true;
    };

    // ── Geofence notification ─────────────────────────────────────────────
    newSocket.on('geofence_notification', (data: any) => {
      if (!acceptEvent(data)) return;
      console.log('🛡️ Geofence notification:', data);

      // Show toast popup immediately
      showToast({
        id: data.id || `toast_${Date.now()}`,
        type: data.type || 'info',
        title: data.title || 'Geofence Alert',
        message: data.message || '',
        priority: data.priority || 'medium',
        category: 'geofence',
        metadata: data.metadata || {},
      });

      // Play sound
      playNotificationSound();

      // Refetch from DB to update the bell
      debouncedRefetch();
    });

    // ── Generic notification ──────────────────────────────────────────────
    newSocket.on('notification', (data: any) => {
      if (!acceptEvent(data)) return;
      console.log('📬 Notification:', data);

      showToast({
        id: data.id || `toast_${Date.now()}`,
        type: data.type || 'info',
        title: data.title || 'Notification',
        message: data.message || '',
        priority: data.priority || 'medium',
        category: data.category || 'system',
        metadata: data.metadata || {},
      });

      if (data.priority === 'critical' || data.priority === 'high') {
        playNotificationSound();
      }

      debouncedRefetch();
    });

    // ── Device alert ──────────────────────────────────────────────────────
    newSocket.on('device_alert', (data: any) => {
      if (!acceptEvent(data)) return;
      console.log('📡 Device alert:', data);

      showToast({
        id: `toast_${Date.now()}`,
        type: 'warning',
        title: data.title || 'Device Alert',
        message: data.message || '',
        priority: 'high',
        category: 'device',
        metadata: data,
      });

      playNotificationSound();
      debouncedRefetch();
    });

    // ── Security alert ────────────────────────────────────────────────────
    newSocket.on('security_alert', (data: any) => {
      if (!acceptEvent(data)) return;
      console.log('🚨 Security alert:', data);

      showToast({
        id: `toast_${Date.now()}`,
        type: 'error',
        title: '🚨 Security Alert',
        message: data.message || 'Unauthorized activity detected',
        priority: 'critical',
        category: 'safety',
        metadata: data,
      });

      playNotificationSound();
      debouncedRefetch();
    });

    setSocket(newSocket);

    return () => {
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      newSocket.close();
    };
  }, [WS_URL, token, debouncedRefetch, showToast]);

  // ─── Load more ───────────────────────────────────────────────────────────
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await fetchNotifications(offsetRef.current, true);
  }, [notifications.length, hasMore, loading, fetchNotifications]);

  // ─── Add (local/manual) ──────────────────────────────────────────────────
  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const n: Notification = {
      ...notification,
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      read: false,
    };
    setNotifications((prev) => [n, ...prev]);
    if (notification.priority === 'critical' || notification.priority === 'high') {
      playNotificationSound();
    }
  };

  // ─── Mark as read ────────────────────────────────────────────────────────
  const markAsRead = async (id: string) => {
    if (notifications.some(n => n.id === id && !n.read)) setServerUnreadCount(count => Math.max(0, count - 1));
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/notifications/${id}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
    } catch { }
  };

  // ─── Mark all as read ────────────────────────────────────────────────────
  const markAllAsRead = async () => {
    setServerUnreadCount(0);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/notifications/mark-all-read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
    } catch { }
  };

  // ─── Remove ──────────────────────────────────────────────────────────────
  const removeNotification = async (id: string) => {
    if (notifications.some(n => n.id === id && !n.read)) setServerUnreadCount(count => Math.max(0, count - 1));
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/notifications/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
    } catch { }
  };

  // ─── Clear all ───────────────────────────────────────────────────────────
  const clearAllNotifications = async () => {
    const ids = notifications.map((n) => n.id);
    setServerUnreadCount(count => Math.max(0, count - notifications.filter(n => !n.read).length));
    setNotifications([]);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/notifications/bulk-action`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notificationIds: ids, action: 'archive' }),
      });
    } catch { }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        hasMore,
        loading,
        toast,
        dismissToast,
        loadMore,
        addNotification,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearAllNotifications,
        socket,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};